// Vue
import { ref, watch } from "vue";

// Dependencies
import { defineStore } from "pinia";

// Types
import { NewBuildingProjectType, useCreateBuildingProjectWizard, useModels, ProjectTemplate } from "@/features/BuildingProject";

// Utils
import { validate } from "@/services/Utils/InputValidation";

// GraphQL
import { useInsertProjectTransportMutation, InsertProjectTransportMutationVariables, Transport_Type_Enum } from "@/graphql";
import withApolloClient from "@/graphql/services/withApolloClient";

const useCreateBuildingProject = defineStore("CreateBuildingProjectProvider", () => {
  // ///////////////////
  // Providers
  // ///////////////////

  const createBuildingProjectWizard = useCreateBuildingProjectWizard();
  const models = useModels();

  // ///////////////////
  // Data
  // ///////////////////

  const projectAddressValidationRules = {
    street: (value: string, isTouched: boolean) =>
      validate(
        "string",
        {
          value,
          isTouched,
          required: true,
        },
        {
          minLength: 2,
          maxLength: 80,
        }
      ),
    streetNumber: (value: string, isTouched: boolean) =>
      validate("string", {
        value,
        isTouched,
        required: true,
      }),
    city: (value: string, isTouched: boolean) =>
      validate("string", {
        value,
        isTouched,
        required: true,
      }),
    postalCode: (value: string, isTouched: boolean) =>
      validate("postalCode", {
        value,
        isTouched,
        required: true,
      }),
    country: (value: string, isTouched: boolean) =>
      validate("country", {
        value,
        isTouched,
        required: true,
      }),
  };

  // ///////////////////
  // Refs
  // ///////////////////

  const project = ref<NewBuildingProjectType>(JSON.parse(JSON.stringify(ProjectTemplate)));
  const projectNameAlreadyExists = ref<boolean>(false);
  const buildingProjectId = ref<string>("");
  // Whether the setup rows the backend cannot compose a project without — project_location and
  // project_calculation — have been written for buildingProjectId. Tracked so a retried create
  // re-attempts those writes on the same project instead of skipping them (RTLCA-5595): the id alone
  // used to stand for "created AND set up", and a project whose setup write failed was unopenable.
  const setupSaved = ref<boolean>(false);
  const validationRules = ref<Record<string, (value: string, isTouched: boolean) => boolean>>(projectAddressValidationRules);

  // ///////////////////
  // Queries
  // ///////////////////

  // Insert project transport
  const { mutate: insertMutation, error: insertError } = withApolloClient(() => useInsertProjectTransportMutation());

  // ///////////////////
  // Getters
  // ///////////////////

  const getProject = () => {
    return project.value;
  };

  const getProjectNameAlreadyExists = () => {
    return projectNameAlreadyExists.value;
  };

  const getBuildingProjectId = () => {
    return buildingProjectId.value;
  };

  const getSetupSaved = () => {
    return setupSaved.value;
  };

  const getValidationRules = (): Record<string, (value: string, isTouched: boolean) => boolean> => validationRules.value;

  // ///////////////////
  // Setters
  // ///////////////////

  const setProjectNameAlreadyExists = (newValue: boolean) => {
    projectNameAlreadyExists.value = newValue;
  };

  const setProject = (newProject: NewBuildingProjectType) => {
    project.value = newProject;
  };

  const setBuildingProjectId = (newBpId: string) => {
    buildingProjectId.value = newBpId;
  };

  const setSetupSaved = (newValue: boolean) => {
    setupSaved.value = newValue;
  };

  const setSpeckleStreamId = (newStreamId: string) => {
    project.value.streamId = newStreamId;
  };

  // ///////////////////
  // Methods
  // ///////////////////

  async function insertProjectTransport() {
    // Map newProject.transportTypes to InsertProjectTransportMutationVariables
    const transportTypesData: InsertProjectTransportMutationVariables = {
      objects: getProject().transportTypes.map((transportType: NewBuildingProjectType["transportTypes"][0]) => {
        return {
          ...(transportType.transport.isDefault
            ? {
                default: {
                  data: [
                    {
                      project_id: getBuildingProjectId(),
                    },
                  ],
                },
              }
            : {}),

          transportId: transportType.transport.id,
          distance: transportType.distance,
          emission: transportType.transport.transportType === Transport_Type_Enum.Custom ? transportType.emission : null,
          project_id: getBuildingProjectId(),
          transport_type_name: transportType.transport_type_name,
        };
      }),
    };

    const data = await insertMutation(transportTypesData);

    return data;
  }

  function resetEmissionFactors() {
    if (!project.value.emissionCalculationAssumptions.projectEnergyData) return;
    project.value.emissionCalculationAssumptions.projectEnergyData.constructional = {
      heat: null,
      electricity: null,
      earthMoved: null,
      machineryFuel: null,
    };
    project.value.emissionCalculationAssumptions.projectEnergyData.operational = {
      heat: null,
      electricity: null,
    };
  }

  // Resets create flow across all providers
  function reset() {
    project.value = JSON.parse(JSON.stringify(ProjectTemplate));
    projectNameAlreadyExists.value = false;
    buildingProjectId.value = "";
    setupSaved.value = false;
    createBuildingProjectWizard.reset();
    models.setModels([]);
  }

  // ///////////////////
  // Utility
  // ///////////////////

  watch(insertError, (newError) => {
    console.error(newError);
  });

  // ///////////////////
  // Return
  // ///////////////////

  return {
    getProject,
    getProjectNameAlreadyExists,
    getBuildingProjectId,
    getSetupSaved,
    getValidationRules,
    setProject,
    setProjectNameAlreadyExists,
    setBuildingProjectId,
    setSetupSaved,
    setSpeckleStreamId,
    insertProjectTransport,
    resetEmissionFactors,
    reset,
  };
});

export default useCreateBuildingProject;
